import type { ImageSettings } from '@ouster/webviz-react-components';

import { DriveOsfInfo } from '../reducers/drive';
import { OSFType } from '../types';

export type CameraShareState = {
  cameraType: 'orthographic' | 'perspective';
  position: [number, number, number];
  target: [number, number, number];
  zoom: number;
};

export type SceneShareState = {
  camera?: CameraShareState;
  pointCloudFlipped?: boolean;
  twoDImagesFlipped?: boolean;
  pointSize?: number;
  pointColorMode?: string;
  pointColorPalette?: string;
  pointAutoLevelsOn?: boolean;
  pointNoiseSuppressionOn?: boolean;
  showRangeRings?: boolean;
  is2DImageVisible?: boolean;
  leftSidebarExpanded?: boolean;
  selectedImagePanelSn?: string;
  selectedSensorImageSettings?: Array<{
    showImage?: boolean;
    coloringMode?: string;
    colorPalette?: string;
    isAutoLevelsOn?: boolean;
    isNoiseSuppressionOn?: boolean;
  }>;
};

export type WorkerFrameFilterInput = {
  frameIdx: number;
  msgReqEpoch?: number;
  requestEpoch: number;
  direction: number;
  lastRenderedFrame: number;
  targetFrame: number;
  isPlaying: boolean;
  isKeyboardActive: boolean;
  keyboardSeekTargetTolerance: number;
};

export type WorkerRequestGateInput = {
  lastInputAgeMs: number;
  manualSeekActiveWindowMs: number;
};

export type FramePreloadGateInput = {
  seeking: boolean;
  isMobileViewport: boolean;
  isPlaying: boolean;
};

export type PausedFrameRequestGateInput = WorkerRequestGateInput & {
  hasWorker: boolean;
  hasSignedUrl: boolean;
  isOpened: boolean;
  isVizInitialized: boolean;
  isPlaying: boolean;
};

export const defaultImagePaletteForMode = (
  coloringMode?: string
): string | undefined => {
  if (!coloringMode) return undefined;
  if (coloringMode === 'RGB') return 'RGB';
  if (coloringMode.startsWith('REFLECTIVITY')) return 'CalRef';
  return 'Greyscale';
};

export const toSharedImageSettings = (
  settings: ImageSettings[],
  userAutoLevelsByImageIndex?: Record<number, boolean>
): NonNullable<SceneShareState['selectedSensorImageSettings']> =>
  settings.map((setting, index) => {
    const mode =
      typeof setting.coloringMode === 'string'
        ? setting.coloringMode
        : String(setting.coloringMode ?? '');
    const defaultPalette = defaultImagePaletteForMode(mode);
    const includePalette =
      typeof setting.colorPalette === 'string' &&
      setting.colorPalette.length > 0 &&
      setting.colorPalette !== defaultPalette;

    return {
      showImage: setting.showImage,
      coloringMode: setting.coloringMode,
      ...(includePalette ? { colorPalette: setting.colorPalette } : {}),
      ...(setting.isAutoLevelsDisabled ||
      !Object.prototype.hasOwnProperty.call(
        userAutoLevelsByImageIndex ?? {},
        index
      )
        ? {}
        : { isAutoLevelsOn: setting.isAutoLevelsOn }),
      ...(setting.isNoiseSuppressionDisabled
        ? {}
        : { isNoiseSuppressionOn: setting.isNoiseSuppressionOn }),
    };
  });

export const toPartialImageSettings = (
  setting: NonNullable<SceneShareState['selectedSensorImageSettings']>[number]
): Partial<ImageSettings> => ({
  ...(setting.showImage !== undefined ? { showImage: setting.showImage } : {}),
  ...(setting.coloringMode !== undefined
    ? { coloringMode: setting.coloringMode }
    : {}),
  ...(setting.colorPalette !== undefined
    ? {
        colorPalette: setting.colorPalette as
          | ImageSettings['colorPalette']
          | undefined,
      }
    : {}),
  ...(setting.isAutoLevelsOn !== undefined
    ? { isAutoLevelsOn: setting.isAutoLevelsOn }
    : {}),
  ...(setting.isNoiseSuppressionOn !== undefined
    ? { isNoiseSuppressionOn: setting.isNoiseSuppressionOn }
    : {}),
});

export const isValidAzimuthWindow = (
  value?: number[]
): value is [number, number] =>
  Array.isArray(value) &&
  value.length === 2 &&
  value.every((entry) => typeof entry === 'number') &&
  value.some((entry) => Math.abs(entry) > 360);

const isValidWorkerFps = (value: number): boolean =>
  Number.isFinite(value) && value > 0 && value <= 240;

export const osfTypeFromString = (osfConfigMode: string) => {
  switch (osfConfigMode) {
    case 'osf':
      return OSFType.legacyOsf;
    case 'binary-osf':
      return OSFType.osf;
    default:
      throw new Error(`OSF mode ${osfConfigMode} is not supported`);
  }
};

export const extractOSFType = (osfInfo: DriveOsfInfo, sectionId: string) =>
  osfTypeFromString(osfInfo.configById[sectionId].mode);

export const extractOSFPath = (osfInfo: DriveOsfInfo, sectionId: string) =>
  `${sectionId}/${osfInfo.configById[sectionId].dataPath}/${sectionId}.osf`;

export const extractOSFOffset = (osfInfo: DriveOsfInfo, sectionId: string) =>
  osfInfo.offsetsById[sectionId];

export const parseDriveOrgId = (orgId?: string): number => {
  if (!orgId) {
    return 0;
  }

  const parsedOrgId = Number(orgId);
  return Number.isFinite(parsedOrgId) ? parsedOrgId : 0;
};

export const getMaxLoadedLocalFrame = (
  currentLocalFrame: number,
  loadedLocalFrames: number[]
) => {
  let nextConsecutive = 0;
  if (loadedLocalFrames && loadedLocalFrames.length > 0) {
    nextConsecutive = currentLocalFrame;
    const loadedFramesSet = new Set(loadedLocalFrames);
    const maxInd = Math.max(...loadedLocalFrames);
    while (nextConsecutive < maxInd) {
      if (!loadedFramesSet.has(nextConsecutive)) {
        break;
      }
      nextConsecutive += 1;
    }
  }
  return nextConsecutive;
};

export const calculateAdjustedFrameRate = (
  baseFrameRate: number,
  imageCount: number,
  speed: number
): number => {
  void imageCount;
  const speedFactor = Math.min(speed ** 1.1, 4);
  const adjustedFrameRate = baseFrameRate * speedFactor;

  return Math.round(adjustedFrameRate);
};

export const shouldUseWorkerFps = (
  workerFps: number | undefined,
  lidarModeFrequency: number
): workerFps is number => {
  if (workerFps === undefined || !isValidWorkerFps(workerFps)) {
    return false;
  }

  if (!Number.isFinite(lidarModeFrequency) || lidarModeFrequency <= 0) {
    return true;
  }

  const ratio = workerFps / lidarModeFrequency;
  return ratio >= 0.35 && ratio <= 2.5;
};

export const shouldIgnoreWorkerFrame = ({
  frameIdx,
  msgReqEpoch,
  requestEpoch,
  direction,
  lastRenderedFrame,
  targetFrame,
  isPlaying,
  isKeyboardActive,
  keyboardSeekTargetTolerance,
}: WorkerFrameFilterInput): boolean => {
  if (typeof msgReqEpoch === 'number' && msgReqEpoch < requestEpoch) {
    return true;
  }

  const isManualSeekSettling =
    !isPlaying && direction !== 0 && lastRenderedFrame !== targetFrame;

  if (
    !isPlaying &&
    isKeyboardActive &&
    direction === 0 &&
    Math.abs(frameIdx - targetFrame) > keyboardSeekTargetTolerance
  ) {
    return true;
  }

  if (isManualSeekSettling) {
    if (direction === -1 && frameIdx > targetFrame + 1) {
      return true;
    }

    if (direction === 1 && frameIdx < targetFrame - 1) {
      return true;
    }

    if (direction === -1 && frameIdx > lastRenderedFrame) {
      return true;
    }

    if (direction === 1 && frameIdx < lastRenderedFrame) {
      return true;
    }
  }

  return false;
};

export const shouldClearManualSeekDirection = (
  frameIdx: number,
  targetFrame: number,
  direction: number,
  isPlaying: boolean
): boolean => !isPlaying && direction !== 0 && frameIdx === targetFrame;

export const isManualSeekActive = ({
  lastInputAgeMs,
  manualSeekActiveWindowMs,
}: WorkerRequestGateInput): boolean =>
  lastInputAgeMs < manualSeekActiveWindowMs;

export const shouldPreloadWorkerFrame = ({
  seeking,
  isMobileViewport,
  isPlaying,
}: FramePreloadGateInput): boolean => {
  const mobilePausedNoPrefetch = isMobileViewport && !isPlaying;

  return !seeking && !mobilePausedNoPrefetch;
};

export const shouldRequestPausedFrame = ({
  hasWorker,
  hasSignedUrl,
  isOpened,
  isVizInitialized,
  isPlaying,
  lastInputAgeMs,
  manualSeekActiveWindowMs,
}: PausedFrameRequestGateInput): boolean => {
  if (
    !hasWorker ||
    !hasSignedUrl ||
    !isOpened ||
    !isVizInitialized ||
    isPlaying
  ) {
    return false;
  }

  return !isManualSeekActive({
    lastInputAgeMs,
    manualSeekActiveWindowMs,
  });
};

export const shouldRequestDebouncedPausedFrame = ({
  isPlaying,
  lastInputAgeMs,
  manualSeekActiveWindowMs,
}: WorkerRequestGateInput & { isPlaying: boolean }): boolean =>
  !isPlaying &&
  !isManualSeekActive({
    lastInputAgeMs,
    manualSeekActiveWindowMs,
  });

export const shouldPollLoadedFrames = ({
  lastInputAgeMs,
  manualSeekActiveWindowMs,
}: WorkerRequestGateInput): boolean =>
  !isManualSeekActive({
    lastInputAgeMs,
    manualSeekActiveWindowMs,
  });
